"use client";
import { motion } from "motion/react";
import IconLink from "../General Components/IconLink";
import SkillIcon from "../About Me/SkillIcon";
import { useFilter } from "@/app/context/FilterContext";

export interface ProjectSkill {
  id: number;
  name: string;
  svgData: string | null;
  color: string;
  invert: boolean;
}

export interface ProjectRow {
  id: number;
  name: string;
  year: string; // formatted display string
  description: string;
  skills: ProjectSkill[];
  githubUrl: string | null;
  hasDownload: boolean;
  filters: { id: number; name: string }[];
}

interface Props {
  project: ProjectRow;
}

const ProjectCard = ({ project }: Props) => {
  const { activeFilter } = useFilter();
  const filterMatch = activeFilter
    ? project.filters.some((f) => f.name === activeFilter)
    : null;

  return (
    <div style={{
      boxShadow: filterMatch === true ? "0 0 28px 6px var(--filter-glow-color)" : "0 0 0px 0px transparent",
      opacity: filterMatch === false ? 0.3 : 1,
      transition: "opacity 0.5s ease-out, box-shadow 0.5s ease-out",
      borderRadius: "16px",
    }}>
    <motion.div
      id={String(project.id)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="bg-(--background2) rounded-[16px] p-8 border border-(--uforeground2)"
    >
      {/* Header row: title left, year + actions right */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-1">
        <div className="flex-1 min-w-0">
          <h2 className="text-4xl font-semibold">{project.name}</h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-(--uforeground2) h-[2px] mt-2"
            style={{ transformOrigin: "left" }}
          />
        </div>

        <div className="flex items-center gap-2 flex-none">
          <span className="text-(--uforeground) text-sm font-medium mr-1">
            {project.year}
          </span>
          {project.githubUrl && (
            <IconLink
              link={project.githubUrl}
              image="/Icons/Websites/Github-Icon.svg"
              imgClassName="flex-none h-7"
            />
          )}
          {project.hasDownload && (
            <a href={`/api/projects/${project.id}/download`} download>
              <motion.button
                className="text-sm px-4 py-2 rounded-[8px] cursor-pointer"
                style={{
                  backgroundColor: "var(--uforeground2)",
                  color: "#ffffff",
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
              >
                Download
              </motion.button>
            </a>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-(--foreground2) mt-4 mb-6 leading-relaxed whitespace-pre-line">
        {project.description}
      </p>

      {/* Skill badges */}
      <div className="flex flex-wrap">
        {project.skills.map((skill) => (
          <SkillIcon
            key={skill.id}
            imageIcon={
              skill.svgData
                ? `data:image/svg+xml,${encodeURIComponent(skill.svgData)}`
                : undefined
            }
            shadowColor={skill.color}
            invert={skill.invert || !skill.svgData}
          >
            {skill.name}
          </SkillIcon>
        ))}
      </div>
    </motion.div>
    </div>
  );
};

export default ProjectCard;
